<template>
  <div v-editable="blok">
    <NavBreadCrumb class="mb-12 lg:mb-16" :title="blok.title" />
    <div class="mx-6 max-w-screen-md text-center md:mx-8 lg:mx-auto">
      <p class="text-sm">
        {{ formattedDate }} |
        <sbUtilReadTime :article-content="blok.articleContent" />
      </p>
      <h1>{{ blok.title }}</h1>

      <p
        v-if="!blok.hideAuthor && blok.author && blok.author.length === 2"
        class="text-sm"
      >
        Written by
        <NuxtLink :to="`/${blogAuthorOneData.full_slug}/`"
          >{{ blogAuthorOneData.content.name }},
          {{ blogAuthorOneData.content.jobTitle }}</NuxtLink
        >
        and
        <NuxtLink :to="`/${blogAuthorTwoData.full_slug}/`"
          >{{ blogAuthorTwoData.content.name }},
          {{ blogAuthorTwoData.content.jobTitle }}</NuxtLink
        >
      </p>
      <div v-else-if="!blok.hideAuthor && blok.author">
        <p class="text-sm">
          Written by
          <NuxtLink :to="`/${blogAuthorOneData.full_slug}/`"
            >{{ blogAuthorOneData.content.name }},
            {{ blogAuthorOneData.content.jobTitle }}</NuxtLink
          >
        </p>
      </div>

      <div class="mb-12 mt-6">
        <SocialButtons :data="socialData" hoz-align="center" />
      </div>
    </div>
    <div
      v-if="!blok.hideImage && blok.image?.filename"
      class="mx-auto mb-12 max-w-screen-xl"
    >
      <img
        :src="`${blok.image.filename}/m/2048x1152/smart/`"
        :alt="blok.image.alt"
        class="w-full xl:rounded-lg"
      />
    </div>

    <div class="rte mx-4 mb-16 max-w-screen-md md:mx-auto">
      <div
        v-if="!blok.hideOverview && blok.overview"
        class="container rounded-2xl bg-green-800"
      >
        <div class="mb-12 max-w-screen-md py-8 text-grey-50">
          <h3 class="!text-xg text-lemon-200 md:!text-2xl">Overview</h3>
          <p class="font-light">{{ blok.overview }}</p>
        </div>
      </div>
      <SbUtilRichTextRender :article-content="blok.articleContent" />
      <p v-if="blok.tags.length > 0" class="mt-8 flex gap-4">
        <span class="font-bold">Tags:</span>
        <span v-for="tag in blok.tags" :key="tag" class="font-light">
          <NuxtLink
            :to="`/blog/tags/${tag.toLowerCase().replaceAll(' ', '-')}/`"
            >{{ tag }}</NuxtLink
          >
        </span>
      </p>
    </div>
    <SbBlogAuthorBlock
      v-if="!blok.hideAuthor && blok.author"
      class="mb-16"
      :author-data="
        blogAuthorTwoData
          ? [blogAuthorOneData, blogAuthorTwoData]
          : [blogAuthorOneData]
      "
    />
    <!-- <SocialSubscribeBlock class="spacing-color" /> -->
    <SbBlogRelatedArticles
      segment="blog"
      :tags="blok.tags"
      :current-story="fullSlug"
    />
  </div>
</template>

<script setup lang="ts">
import type { ISbRichtext, ISbStoryData } from "storyblok-js-client";

const props = defineProps<{
  blok: {
    title: string;
    hideAuthor: boolean;
    author: string;
    publishedDate: string;
    tags: string[];
    hideOverview: boolean;
    overview: string;
    articleContent: ISbRichtext;
    hideImage: boolean;
    image?: {
      filename: string;
      alt: string;
    };
  };
  publishedAt?: string;
  parentId?: number;
  fullSlug?: string;
}>();

const formattedDate = new Date(props.blok.publishedDate).toLocaleDateString(
  "en-au",
  {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  },
);

const socialData = {
  facebook: {
    share: true,
  },
  threads: {
    share: true,
  },
  twitter: {
    share: true,
  },
  linkedin: {
    share: true,
  },
  email: {
    share: true,
  },
};

const blogAuthorOneData = ref();
const blogAuthorTwoData = ref();

if (props.blok.author && props.blok.author.length > 0) {
  const contactData = ref<ISbStoryData[]>();
  const authorsSchema: { "@type": string; name: string; url: string }[] = [];
  const googleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: props.blok.title,
    image: [props.blok.image?.filename],
    datePublished: props.blok.publishedDate,
    dateModified: props.publishedAt,
    author: [{}],
  };

  contactData.value = await getStoryblokDataUUID(
    Array.isArray(props.blok.author)
      ? `${props.blok.author[0]},${props.blok.author[1]}`
      : props.blok.author,
  );

  const authorOne = Array.isArray(props.blok.author)
    ? props.blok.author[0]
    : props.blok.author;

  contactData.value?.forEach((el) => {
    if (el.uuid === authorOne) blogAuthorOneData.value = el;
    if (el.uuid === props.blok.author[1]) blogAuthorTwoData.value = el;
  });

  contactData.value?.forEach((author) => {
    if (author.uuid === "1cdaf6c1-ea8a-4624-8a5a-2c1ef7eb2298") {
      authorsSchema.push({
        "@type": "organization",
        name: author.name,
        url: `https://www.aussiebroadband.com.au/${author.full_slug}/`,
      });
    } else {
      authorsSchema.push({
        "@type": "person",
        name: author.name,
        url: `https://www.aussiebroadband.com.au/${author.full_slug}/`,
      });
    }
  });

  googleSchema.author = authorsSchema;

  useHead({
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify(googleSchema),
        tagPosition: "bodyClose",
      },
    ],
  });
}
</script>
